import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { ArrowLeft, Loader2 } from 'lucide-react';
import type { BookingSlot } from '../../services/interviewBookingApi';
import { emailLooksValid, fmtLongDay, linkedinUrlLooksValid, slotRange, TZ } from './format';
import { InlineError } from './BookingPanels';

const MAX_NAME_LENGTH = 120;

/**
 * Step 2: the candidate's own details.
 *
 * The email is required and is the ONLY address the invite goes to — nothing
 * upstream knows it, since this flow starts from a link rather than from a
 * candidate record.
 */
export function DetailsForm({
  slot,
  minutes,
  candidateName,
  candidateEmail,
  onName,
  onEmail,
  submitting,
  error,
  onBack,
  onConfirm,
  linkedinUrl,
  onLinkedinUrl,
}: {
  slot: BookingSlot;
  minutes: number;
  candidateName: string;
  candidateEmail: string;
  onName: (v: string) => void;
  onEmail: (v: string) => void;
  submitting: boolean;
  error: string | null;
  onBack: () => void;
  onConfirm: () => void;
  /** Asked only on the LinkedIn link, where the prospect is usually not in Lever yet. */
  linkedinUrl?: string;
  onLinkedinUrl?: (v: string) => void;
}) {
  const emailTouched = candidateEmail.trim() !== '';
  const emailValid = emailLooksValid(candidateEmail);
  const askLinkedin = Boolean(onLinkedinUrl);
  const linkedinValid = !askLinkedin || linkedinUrlLooksValid(linkedinUrl ?? '');
  const canSubmit = candidateName.trim().length > 0 && emailValid && linkedinValid;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit && !submitting) onConfirm();
      }}
    >
      <Button size="sm" variant="ghost" type="button" onClick={onBack} className="mb-3">
        <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
        Back
      </Button>

      <h3 className="text-sm font-semibold text-gray-900">Confirm your details</h3>
      <p className="mt-1 text-sm text-gray-600">
        {fmtLongDay(slot.start)} · {slotRange(slot)} ({minutes} min, {TZ})
      </p>

      <div className="mt-4 space-y-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-700">Full name</span>
          <Input
            value={candidateName}
            onChange={(e) => onName(e.target.value)}
            placeholder="Jane Doe"
            maxLength={MAX_NAME_LENGTH}
            required
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-700">
            Email for the calendar invite
          </span>
          <Input
            type="email"
            value={candidateEmail}
            onChange={(e) => onEmail(e.target.value)}
            placeholder="jane@example.com"
            required
          />
          {emailTouched && !emailValid ? (
            <span className="mt-1 block text-xs text-red-600">
              Please enter a valid email address.
            </span>
          ) : (
            <span className="mt-1 block text-xs text-gray-500">
              The invite and the Google Meet link go to this address.
            </span>
          )}
        </label>

        {askLinkedin ? (
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-700">LinkedIn profile URL</span>
            <Input
              type="url"
              value={linkedinUrl ?? ''}
              onChange={(e) => onLinkedinUrl?.(e.target.value)}
              placeholder="linkedin.com/in/jane-doe"
              required
            />
            {(linkedinUrl ?? '').trim() && !linkedinValid ? (
              <span className="mt-1 block text-xs text-red-600">
                Please enter your profile URL, like linkedin.com/in/jane-doe.
              </span>
            ) : null}
          </label>
        ) : null}
      </div>

      {error ? <InlineError message={error} /> : null}

      <Button type="submit" className="mt-4" disabled={!canSubmit || submitting}>
        {submitting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
        {submitting ? 'Booking…' : 'Confirm interview'}
      </Button>
    </form>
  );
}
